import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { enhancedApi } from '@/services/api/generated/postgridService';

interface RecentMailWarningProps {
  matterId: string;
  contactId: string;
  /** Number of days to look back for recent mail (default: 30) */
  lookbackDays?: number;
}

export const RecentMailWarning: React.FC<RecentMailWarningProps> = ({
  matterId,
  contactId,
  lookbackDays = 30,
}) => {
  // Fetch recent letters for this matter
  const { data: letters = [] } = enhancedApi.usePostgridLettersListQuery(
    { matterId },
    {
      skip: !matterId || !contactId,
      refetchOnMountOrArgChange: true,
    },
  );

  // Fetch recent cheques for this matter
  const { data: cheques = [] } = enhancedApi.usePostgridChequesListQuery(
    { matterId },
    {
      skip: !matterId || !contactId,
      refetchOnMountOrArgChange: true,
    },
  );

  // Filter for items sent to this specific contact within the lookback period
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - lookbackDays);

  const recentLetters = letters.filter((letter) => {
    if (letter.to_contact_id !== contactId) return false;
    if (!letter.created_at) return false;

    const createdDate = new Date(letter.created_at);
    return createdDate >= cutoffDate;
  });

  const recentCheques = cheques.filter((cheque) => {
    if (cheque.to_contact_id !== contactId) return false;
    if (!cheque.created_at) return false;

    const createdDate = new Date(cheque.created_at);
    return createdDate >= cutoffDate;
  });

  // If no recent mail, don't show warning
  if (recentLetters.length === 0 && recentCheques.length === 0) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'delivered':
      case 'completed':
        return 'text-green-700';
      case 'cancelled':
      case 'failed':
        return 'text-red-700';
      case 'creating':
      case 'ready':
      case 'queued':
      case 'processing':
        return 'text-blue-700';
      default:
        return 'text-gray-700';
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-sm font-medium text-amber-800 mb-2">
            Recent Mail Sent to This Contact
          </h4>
          <p className="text-sm text-amber-700 mb-3">
            We recently sent mail to this contact for this matter. Please review
            before sending again.
          </p>

          <div className="space-y-2">
            {recentLetters.map((letter) => (
              <div key={letter.id} className="bg-amber-100 rounded px-3 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-amber-800 bg-amber-200 px-2 py-1 rounded">
                      LETTER
                    </span>
                    <span className="text-sm text-amber-800">
                      {letter.subject || 'Untitled Letter'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-amber-600">
                      {letter.created_at && formatDate(letter.created_at)}
                    </span>
                    <span
                      className={`font-medium ${getStatusColor(letter.status)}`}
                    >
                      {letter.status?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {recentCheques.map((cheque) => (
              <div key={cheque.id} className="bg-amber-100 rounded px-3 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-amber-800 bg-amber-200 px-2 py-1 rounded">
                      CHEQUE
                    </span>
                    <span className="text-sm text-amber-800">
                      ${cheque.amount} {cheque.memo && `- ${cheque.memo}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-amber-600">
                      {cheque.created_at && formatDate(cheque.created_at)}
                    </span>
                    <span
                      className={`font-medium ${getStatusColor(cheque.status)}`}
                    >
                      {cheque.status?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
